import { Changeset } from 'ember-changeset';
import lookupValidator from 'ember-changeset-validations';
import { BufferedChangeset } from 'ember-changeset/types';
import { restartableTask } from 'ember-concurrency';
import { buildCustomFieldValues } from 'volta/models/custom-field-definition';
import { ResourceName as PlanningSettingResourceName } from 'volta/models/planning-setting';
import SkuModel, { ResourceName as SkuResourceName, SkuEvents } from 'volta/models/sku';
import { SkuTaskResourceName, TaskListChangeEvents, UTask } from 'volta/models/task';
import UpdatePlanningSettingsCmd from 'volta/models/update-planning-settings-cmd';
import { normalize } from 'volta/utils/api/serialize-and-push';
import { defaultErrorHandler, IJSONAPIErrors, TError } from 'volta/utils/error-utils';
import { parseNumber } from 'volta/utils/math-utils';
import SkuUpdateValidations from 'volta/validations/update-sku';

import { action } from '@ember/object';
import { inject as service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';

import type CustomFieldDefinition from 'volta/models/custom-field-definition';
import type Part from 'volta/models/part';
import type PartType from 'volta/models/part-type';
import type PlanningSetting from 'volta/models/planning-setting';

import type Simulation from 'volta/models/simulation';
import type Supplier from 'volta/models/supplier';
import type { TPlanningMethodology } from 'volta/models/types/planning-settings';
import type Warehouse from 'volta/models/warehouse';
import type Workshop from 'volta/models/workshop';
import type AuthorizationService from 'volta/services/authorization';
import type { Router } from '@ember/routing';
import type IntlService from 'ember-intl/services/intl';
import type AppSettings from 'volta/services/app-settings';
import type BvFlashService from 'volta/services/bv-flash';
import type BvMetricsService from 'volta/services/bv-metrics';
import type CustomFieldDefinitionsService from 'volta/services/custom-field-definitions-service';
import type EventStreamService from 'volta/services/event-stream';
import type LabelService from 'volta/services/label-service';
import type SkuModalService from 'volta/services/sku-modal-service';
import type StoreService from 'volta/services/store';
import type TaskService from 'volta/services/task-service';
import { IPartTypeInfo } from 'volta/models/types/sku';
interface IArgs {
  skuId?: string;
  sku?: SkuModel;
  initialSkuAttrs?: any;
  inModal: boolean;
  embedded: boolean;
  onChange?: (isDirty: boolean) => void;
  onClose?: () => void;
}

const MethodologyToBlocks: Record<TPlanningMethodology, string[]> = {
  BUFFER: ['OrderCycle', 'BufferProfile', 'AduProfile', 'OrderSpike'],
  MRP: ['MrpSettings'],
  NONE: ['UnmanagedSettings']
};

export default class SkuDetailsSettings extends Component<IArgs> {
  // Services
  // ~~~~

  @service authorization!: AuthorizationService;
  @service store!: StoreService;
  @service router!: Router;
  @service intl!: IntlService;
  @service eventStream!: EventStreamService;
  @service bvFlash!: BvFlashService;
  @service bvMetrics!: BvMetricsService;
  @service skuModalService!: SkuModalService;
  @service labelService!: LabelService;
  @service appSettings!: AppSettings;
  @service customFieldDefinitionsService!: CustomFieldDefinitionsService;
  @service taskService!: TaskService;

  // State
  // ~~~~

  @tracked sku?: SkuModel;
  @tracked planningSettings?: PlanningSetting;
  @tracked isConfirmModalShowing: boolean = false;
  @tracked showNewSimulationModal: boolean = false;

  @tracked newTag?: string;
  @tracked tagsModified = false;
  @tracked changeset?: BufferedChangeset;
  @tracked backendErrors?: object;
  @tracked customFieldsAreInvalid: boolean = false;
  @tracked settingsSaving = false;
  @tracked skuSaving = false;

  isCreateMode: boolean = !this.args.skuId && !this.args.sku;
  resourceName = SkuResourceName;

  // Computed Properties
  // ~~~~~

  /**
   * This getter checks whether we should show the planning settings screen at all
   * by checking if at least one block related to the given methodology is visible
   */
  get canViewPlanningsettings() {
    if (!this.planningSettings) {
      return false;
    }

    const { methodology = 'NONE' } = this.planningSettings.settings;

    return this.authorization.canViewAnyDetails(
      'planningSettings',
      ...MethodologyToBlocks[methodology]
    );
  }

  get layout() {
    const { dashboard = [], hiddenFields = [] } = this.appSettings.features?.sku ?? {};
    const hf: Record<string, boolean> = {};
    hiddenFields.forEach((f: string) => {
      hf[f] = true;
    });

    return {
      primary: dashboard.filter((p) => p.enabled && p.layout === 'primary'),
      secondary: dashboard.filter((p) => p.enabled && p.layout === 'secondary'),
      hiddenFields: hf
    };
  }

  /**
   * Get the sku display name which is either its description
   * (depending on whether we're create mode is the sku description or part description)
   * when defined or the part code
   */
  get displayName() {
    return this.changeset?.get('description') ?? this.changeset?.get('part.code');
  }

  /**
   * Concatenated tags and part tags
   */
  get allTags(): Array<{ tag: string; removable?: boolean }> {
    const cs = this.changeset;
    if (!cs) {
      return [];
    }

    const tags = cs.get('tags') ?? [];
    const partTags = cs.get('part.tags') ?? [];
    return partTags
      .map((tag: string) => {
        return { tag };
      })
      .concat(
        tags.map((tag: string) => {
          return { tag, removable: true };
        })
      )
      .sortBy('tag');
  }

  /**
   * Get the config for the selected warehouse of the default one
   */
  get warehouseConfig() {
    const warehouseId: string | undefined = this.changeset?.get('warehouse.warehouseId');
    return warehouseId ? this.appSettings.byWarehouseOrDefault(warehouseId) : undefined;
  }

  /**
   * Get the default PO type for the selected warehouse
   */
  get defaultPOType() {
    return this.warehouseConfig?.settings.poWorkbench.defaultSKUOrderType;
  }

  /**
   * The list of skus all custom fields definitions
   */
  get skuCustomFields(): CustomFieldDefinition[] | undefined {
    return this.customFieldDefinitionsService.skusDefinitions;
  }

  /**
   * The list of skus authorized custom fields definitions for the current SKU
   */
  get authorizedCustomFields() {
    const { skuCustomFields, changeset } = this;

    if (!changeset) {
      return [];
    }
    const warehouseId = changeset.get('warehouse.warehouseId');
    const acqCode = changeset.get('acqCode');
    return skuCustomFields
      ? skuCustomFields.filter((def) => def.isAuthorized(warehouseId, acqCode)).sortBy('order')
      : [];
  }

  get customFieldAreInvalid() {
    return this.customFieldsAreInvalid || this.changeset?.change.customFields;
  }

  // Lifecycle Hooks
  // ~~~~

  constructor(owner: any, args: IArgs) {
    super(owner, args);
    const es = this.eventStream;
    es.addHandler(
      SkuResourceName.plural,
      SkuEvents.VLTCalculationDone,
      this._onVLTCalculationDone,
      this
    );
    es.addHandler(SkuResourceName.plural, TaskListChangeEvents, this.handleTaskEvent, this);
  }

  willDestroy() {
    this.eventStream.removeHandlers(this);
  }

  // Actions
  // ~~~~~

  handleInsert = () => {
    if (this.args.initialSkuAttrs || this.isCreateMode) {
      this.initChangeset();
    }

    this.refreshData.perform();
  };

  /**
   * We make sure to keep the existing part type and unit measure if the received part is new
   *
   * @param {Part} part Either a new model (not yet persisted) or an existing one
   */
  @action
  handlePartChange(part: Part) {
    this.bvMetrics.trackAction(this, 'handlePartChange', part.code);
    part.unitMeasure = part.unitMeasure ?? this.changeset?.get('part.unitMeasure') ?? 'UNIT';
    this.changeset?.set('part', part);
    this.changeset?.set('partType', part.partType ?? this.changeset?.get('partType'));
    this.args.onChange?.(Boolean(this.changeset?.isDirty));
  }

  @action
  handleDescriptionChange(value: string) {
    this.bvMetrics.trackAction(this, 'handleDescriptionChange', value);
    this.changeset?.set('description', value === '' ? null : value);
    this.args.onChange?.(Boolean(this.changeset?.isDirty));
  }

  @action
  handleAcqCodeChange(value: string) {
    this.bvMetrics.trackAction(this, 'handleAcqCodeChange', value);
    this.changeset?.set('acqCode', value);
    this.args.onChange?.(Boolean(this.changeset?.isDirty));
  }

  @action
  handleOrderTypeChange(value: string) {
    this.bvMetrics.trackAction(this, 'handleOrderTypeChange', value);
    this.changeset?.set('orderType', value);
    this.args.onChange?.(Boolean(this.changeset?.isDirty));
  }

  @action
  handleLeadTimeChange(value: string) {
    this.bvMetrics.trackAction(this, 'handleLeadTimeChange', value);
    this.changeset?.set('leadTime', parseNumber(value));
    this.args.onChange?.(Boolean(this.changeset?.isDirty));
  }

  @action
  handleInternalLeadTimeChange(value: string) {
    this.bvMetrics.trackAction(this, 'handleInternalLeadTimeChange', value);
    this.changeset?.set('internalLeadTime', parseNumber(value));
    this.args.onChange?.(Boolean(this.changeset?.isDirty));
  }

  @action
  handleMOQChange(value: string) {
    this.bvMetrics.trackAction(this, 'handleMOQChange', value);
    this.changeset?.set('minOrderQty', parseNumber(value));
    this.args.onChange?.(Boolean(this.changeset?.isDirty));
  }

  @action
  handlePriceChange(value: string) {
    this.bvMetrics.trackAction(this, 'handlePriceChange', value);
    this.changeset?.set('inventoryUnitPrice', parseNumber(value));
    this.args.onChange?.(Boolean(this.changeset?.isDirty));
  }

  @action
  handleSalesPriceChange(value: string) {
    this.bvMetrics.trackAction(this, 'handleSalesPriceChange', value);
    this.changeset?.set('salesUnitPrice', parseNumber(value));
    this.args.onChange?.(Boolean(this.changeset?.isDirty));
  }

  @action
  handleOrderMultipleChange(value: string) {
    this.bvMetrics.trackAction(this, 'handleOrderMultipleChange', value);
    this.changeset?.set('orderMultiple', parseNumber(value));
    this.args.onChange?.(Boolean(this.changeset?.isDirty));
  }

  @action
  handleMaxPOQtyChange(value: string) {
    this.bvMetrics.trackAction(this, 'handleMaxPOQtyChange', value);
    this.changeset?.set('maxPOQty', parseNumber(value));
    this.args.onChange?.(Boolean(this.changeset?.isDirty));
  }

  @action
  handlePartTypeChange(_partTypeId: string, partType: PartType | IPartTypeInfo) {
    this.bvMetrics.trackAction(this, 'handlePartTypeChange', partType?.code);
    this.changeset?.set('partType', partType);
    this.args.onChange?.(Boolean(this.changeset?.isDirty));
  }

  @action
  handleUnitMeasureChange(unitMeasure: string) {
    this.bvMetrics.trackAction(this, 'handleUnitMeasureChange', unitMeasure);
    const part = this.changeset?.part ?? this.store.createRecord(SkuResourceName.singular);
    part.unitMeasure = unitMeasure;
    this.changeset?.set('part', part);
    this.args.onChange?.(Boolean(this.changeset?.isDirty));
  }

  @action
  changeDeliveryWorkshop(_workshopId: string, workshop: Workshop) {
    this.bvMetrics.trackAction(this, 'changeDeliveryWorkshop', workshop?.code);
    this.changeset?.set('deliveryWorkshop', workshop);
    this.args.onChange?.(Boolean(this.changeset?.isDirty));
  }

  @action
  changeWorkshop(_workshopId: string, workshop: Workshop) {
    this.bvMetrics.trackAction(this, 'changeWorkshop', workshop?.code);
    this.changeset?.set('workshop', workshop);
    this.args.onChange?.(Boolean(this.changeset?.isDirty));
  }

  @action
  changeSupplier(_supplierId: string, supplier: Supplier) {
    this.bvMetrics.trackAction(this, 'changeSupplier', supplier?.code);
    this.changeset?.set('supplier', supplier);
    this.args.onChange?.(Boolean(this.changeset?.isDirty));
  }

  /**
   * Action handling warehouse modifications. This function should only be called when creating a SKU
   * We initialise the order type if the warehouse has changed to the default one from the warehouse config
   */
  @action
  changeWarehouse(_warehouseId: string, warehouse: Warehouse) {
    const cs = this.changeset;
    if (!cs) {
      return;
    }
    this.bvMetrics.trackAction(this, 'changeWarehouse', warehouse?.code);
    if (cs.get('warehouse.code') !== warehouse?.code) {
      cs.set('warehouse', warehouse);
      cs.set('orderType', this.defaultPOType);
    }
    this.args.onChange?.(Boolean(this.changeset?.isDirty));
  }

  @action
  showConfirmModal() {
    this.bvMetrics.trackAction(this, 'showConfirmModal');
    if (!this.customFieldsAreInvalid && this.changeset?.isValid) {
      this.isConfirmModalShowing = true;
    }
  }

  @action
  closeConfirmModal() {
    this.bvMetrics.trackAction(this, 'closeConfirmModal');
    this.isConfirmModalShowing = false;
  }

  @action
  async handleSaveSku() {
    this.bvMetrics.trackAction(this, 'handleSaveSku');
    if (this.sku?.id) {
      return await this.handleUpdateSku();
    } else {
      return await this.handleCreateSku();
    }
  }

  @action
  async handleCreateSku() {
    this.bvMetrics.trackAction(this, 'handleCreateSku');
    const cs = this.changeset;

    if (cs) {
      cs.validate();
      const payload = this.buildBasePayload(cs, true);
      if (cs.isValid && payload) {
        payload.warehouseId = cs.get('warehouse.warehouseId');
        payload.partId = cs.get('part.partId');
        payload.partCode = cs.get('part.code');
        payload.description = cs.get('part.description') ?? cs.get('description');
        payload.partUnitMeasure = cs.get('part.unitMeasure');
        payload.partTypeId = cs.get('partType.partTypeId');

        try {
          cs.execute();
          const createdSku = (await SkuModel.createSku(payload)) as SkuModel;
          this._onUpdateSkuSuccess(createdSku);
          this.router.transitionTo('protected.sku', createdSku.id);
        } catch (error) {
          this._onCreateSkuFailure(error);
        }
      }
    }
  }

  @action
  async handleUpdateSku() {
    this.bvMetrics.trackAction(this, 'handleUpdateSku');
    const cs = this.changeset;
    if (!cs) {
      return;
    }

    cs.validate();

    if (cs.isInvalid) {
      return;
    }

    const payload = this.buildBasePayload(cs);

    if (!payload || !this.sku) {
      return undefined;
    }

    try {
      cs.execute();
      const updatedSku = await this.sku.updateSku(payload);
      return this._onUpdateSkuSuccess(updatedSku);
    } catch (error) {
      return this._onUpdateSkuFailure(error);
    }
  }

  @action
  async savePlanningSettings(settings: PlanningSetting, cmd: UpdatePlanningSettingsCmd) {
    this.bvMetrics.trackAction(this, 'savePlanningSettings');
    this.settingsSaving = true;

    try {
      const payload = cmd.toNative();
      // Because the changes are not initially made on the model itself
      // but on a command data structure, Ember-data is not aware of the changes.
      // If a property that had a value has been put to null like for example "fixedGreen",
      // when Ember-data will push the updated record returned by the back-end to the store,
      // the value won't be updated, because the property returned is undefined (not null)
      // Ember-data lacks a way of pushing a record to the store
      // that completely replaces the existing record not merely merging the props
      settings.setProperties({ ...payload });
      const updatedSettings = await settings.updatePlanningSettings(cmd.toNative());
      return this._onUpdatePlanningSettingsSuccess(updatedSettings);
    } catch (error) {
      return this._onUpdatePlanningSettingsFailure(error);
    }
  }

  /**
   * Add a new tag to the changeset
   * @param tagsToAdd The tag to add
   */
  @action
  addSkuTag(tagsToAdd: string | string[]) {
    const tag = typeof tagsToAdd === 'string' ? tagsToAdd : tagsToAdd[0];
    this.bvMetrics.trackAction(this, 'addSkuTag', tag);
    const tags: string[] = (this.changeset?.get('tags') ?? []).slice();
    if (!tags.includes(tag)) {
      tags.push(tag);
      this.changeset?.set('tags', [...tags]);
      this.args.onChange?.(Boolean(this.changeset?.isDirty));
    }
  }

  /**
   * Removes a tag from the changeset
   * @param tag Tag to remove
   */
  @action
  removeSkuTag(tag: string) {
    this.bvMetrics.trackAction(this, 'removeSkuTag', tag);
    const tags = (this.changeset?.get('tags') ?? []).filter((t: string) => t !== tag);
    this.changeset?.set('tags', [...tags]);
    this.args.onChange?.(Boolean(this.changeset?.isDirty));
  }

  @action
  resetSkuTags() {
    this.bvMetrics.trackAction(this, 'resetSkuTags');
    this.changeset?.rollbackProperty('tags');
    this.args.onChange?.(Boolean(this.changeset?.isDirty));
  }

  @action
  handleDuplicateSku() {
    this.bvMetrics.trackAction(this, 'handleDuplicateSku');
    this.router.transitionTo('protected.sku-duplicate', this.args.sku);
  }

  @action
  handleCustomFieldsChange(values: Record<string, any>, isInvalid: boolean) {
    this.bvMetrics.trackAction(this, 'handleCustomFieldsChange');
    this.customFieldsAreInvalid = isInvalid;
    this.changeset?.set('customFields', { ...values });
    this.args.onChange?.(Boolean(this.changeset?.isDirty));
  }

  @action
  handleCopyDuplicatedPart() {
    this.bvMetrics.trackAction(this, 'handleCopyDuplicatedPart');
    const { initialSkuAttrs } = this.args;
    if (initialSkuAttrs) {
      this.changeset?.set('part', { ...initialSkuAttrs.part });
      this.args.onChange?.(Boolean(this.changeset?.isDirty));
    }
  }

  @action
  handleDiscardEdits() {
    this.bvMetrics.trackAction(this, 'handleDiscardEdits');
    this.skuModalService.unsavedSkuEdits = false;
    this.args.onChange?.(false);
    return this.args.onClose?.();
  }

  // Helper Functions
  // ~~~~~

  initChangeset(sku?: SkuModel) {
    let { initialSkuAttrs } = this.args;
    initialSkuAttrs = initialSkuAttrs ? { ...initialSkuAttrs, ...{ part: undefined } } : undefined;
    this.sku =
      sku ??
      this.store.createRecord(SkuResourceName.singular, {
        orderType: this.defaultPOType,
        ...(initialSkuAttrs ?? {})
      });

    this.changeset = Changeset(
      this.sku as SkuModel,
      lookupValidator(SkuUpdateValidations),
      SkuUpdateValidations
    );

    if (initialSkuAttrs) {
      this.changeset.validate();
    }

    // This should only be executed when creating a NEW article not an update, not a duplication
    if (this.isCreateMode && !sku && !initialSkuAttrs && this.changeset) {
      const initialCustomFields = this.authorizedCustomFields.reduce(
        (acc, field: CustomFieldDefinition) => {
          if (field.required) {
            acc[field.id] = field.defaultValue;
          }
          return acc;
        },
        {} as { [id: string]: any }
      );
      this.changeset.prepare(() => ({ customFields: initialCustomFields }));
      this.changeset.execute();
    }
  }

  /**
   * Build the payload for the CreateSku and UpdateSku commands
   *
   * @param {Changeset} cs SKU Create/Update Changeset
   * @param {boolean} createMode If true we should include all fields,
   *                             because if the sku is created from a duplication
   *                             only a few fields will have been modified
   * @returns {Object}
   */
  buildBasePayload(cs: any, createMode: boolean = false): Record<string, any> | undefined {
    if (!cs || cs.isPristine) {
      return undefined;
    }

    cs.validate();

    if (cs.isInvalid) {
      return undefined;
    }

    const payload: Record<string, any> = {};
    this.skuSaving = true;
    const changedFields = cs.changes?.map((c: any) => c.key);

    [
      'internalLeadTime',
      'leadTime',
      'inventoryUnitPrice',
      'salesUnitPrice',
      'maxPOQty',
      'orderMultiple'
    ].forEach((attr) => {
      if (changedFields.includes(attr) || createMode) {
        payload[attr] = parseNumber(cs.get(attr));
      }
    });
    if (changedFields.includes('minOrderQty') || createMode) {
      payload.minOrderQty = parseNumber(cs.minOrderQty) ?? 0;
    }
    if (changedFields.includes('acqCode') || createMode) {
      payload.acqCode = cs.acqCode;
    }
    if (changedFields.includes('description') || createMode) {
      payload.description = cs.description;
    }
    if (changedFields.includes('tags') || createMode) {
      payload.tags = cs.tags;
    }
    if (changedFields.includes('deliveryWorkshop') || createMode) {
      payload.deliveryWorkshopId = cs.deliveryWorkshop?.workshopId;
    }
    if (changedFields.includes('workshop') || createMode) {
      payload.workshopId = cs.workshop?.workshopId;
    }
    if (changedFields.includes('supplier') || createMode) {
      payload.supplierId = cs.supplier?.supplierId;
    }
    if (changedFields.includes('orderType') || createMode) {
      payload.orderType = cs.orderType;
    }
    if (changedFields.includes('customFields') || createMode) {
      const { authorizedCustomFields } = this;
      payload.customFields = cs.customFields
        ? buildCustomFieldValues(authorizedCustomFields, cs.customFields)
        : undefined;
    }

    return payload;
  }

  async handleTaskEvent(payload: any) {
    const { skuId } = this.args;

    const task = normalize(SkuTaskResourceName.singular, payload) as UTask;

    if ((task.taskType === 'SKU_TASK' || task.taskType === 'COMMENT') && skuId) {
      await this.store.findRecord(SkuResourceName.singular, skuId);
    }
  }

  @action
  onSimulationCreateSuccess(simulation: Simulation) {
    this.showNewSimulationModal = false;
    this.router.transitionTo('protected.simulation', simulation.id);
  }

  refreshData = restartableTask(async () => {
    const { skuId, sku } = this.args;

    try {
      await this.customFieldDefinitionsService.task.perform(SkuResourceName.table);
      if (sku) {
        this.initChangeset(sku);
      }

      if (skuId) {
        if (!this.sku) {
          await this.getSkuTask.perform(skuId);
        }
        if (this.sku && this.sku.isActive) {
          await this.getPlanningSettingsTask.perform(skuId);
        }
      }
    } catch (error) {
      defaultErrorHandler(error);
      return;
    }
  });

  refreshSku = restartableTask(async (skuId: string) => {
    try {
      const sku: SkuModel = await this.store.findRecord(SkuResourceName.singular, skuId);
      return sku;
    } catch (error) {
      defaultErrorHandler(error);
      return;
    }
  });

  getSkuTask = restartableTask(async (skuId: string) => {
    const sku: SkuModel | undefined = await this.refreshSku.perform(skuId);
    this.initChangeset(sku);
    return sku;
  });

  getPlanningSettingsTask = restartableTask(async (skuId: string) => {
    try {
      this.planningSettings = await this.store.findRecord(
        PlanningSettingResourceName.singular,
        skuId,
        { adapterOptions: { query: { include: 'bufferProfile,aduProfile,dafProfile' } } }
      );

      return this.planningSettings;
    } catch (error) {
      defaultErrorHandler(error);
      return;
    }
  });

  // Private Callbacks
  // ~~~~~

  _onUpdatePlanningSettingsSuccess(updatedSettings: PlanningSetting) {
    if (this.isDestroyed || this.isDestroying) {
      return;
    }
    this.settingsSaving = false;
    this.bvFlash.success(
      this.intl.t('bvList.singleActions.updateSuccess', {
        resourceNameSingular: this.intl.t('sku'),
        name: this.sku?.partCode
      })
    );
    this.planningSettings = updatedSettings;
    return updatedSettings;
  }

  _onUpdatePlanningSettingsFailure(error: TError) {
    defaultErrorHandler(error);
    if (this.isDestroyed || this.isDestroying) {
      return;
    }
    this.settingsSaving = false;
    this.bvFlash.apiError(error);
  }

  _onVLTCalculationDone(payload: any) {
    if (this.isDestroying || this.isDestroyed) {
      return;
    }
    const { skuId } = this.args;
    if (skuId && payload.skuIds.indexOf(skuId) !== -1) {
      this.getSkuTask.perform(skuId);
    }
  }

  _onUpdateSkuSuccess(updatedSku: SkuModel) {
    if (this.isDestroyed || this.isDestroying) {
      return;
    }
    this.initChangeset(updatedSku);
    this.skuSaving = false;
    this.skuModalService.unsavedSkuEdits = false;
    this.bvFlash.success(
      this.intl.t('bvList.singleActions.updateSuccess', {
        resourceNameSingular: this.intl.t('sku'),
        name: this.sku?.partCode
      })
    );

    this.isConfirmModalShowing = false;
    this.backendErrors = undefined;
    this.customFieldsAreInvalid = false;
    this.args.onChange?.(Boolean(this.changeset?.isDirty));
    return updatedSku;
  }

  _onCreateSkuFailure(error: IJSONAPIErrors) {
    defaultErrorHandler(error);
    if (this.isDestroyed || this.isDestroying) {
      return;
    }
    this.skuSaving = false;
    this.skuModalService.unsavedSkuEdits = false;
    const backendErrors = this.labelService.formValidationsFromApiError(error);
    if (backendErrors) {
      this.backendErrors = backendErrors;
    }
    this.bvFlash.error(
      this.intl.t('bvList.singleActions.createFail', {
        resourceNameSingular: this.intl.t(SkuResourceName.singular).toLowerCase(),
        name: this.sku?.partCode
      })
    );
  }

  _onUpdateSkuFailure(error: IJSONAPIErrors) {
    defaultErrorHandler(error);
    if (!this.isDestroyed && !this.isDestroying) {
      this.skuSaving = false;
      this.skuModalService.unsavedSkuEdits = false;
      const backendErrors = this.labelService.formValidationsFromApiError(error);
      if (backendErrors) {
        this.backendErrors = backendErrors;
      }
      this.bvFlash.error(
        this.intl.t('bvList.singleActions.updateFail', {
          resourceNameSingular: this.intl.t(SkuResourceName.singular).toLowerCase(),
          name: this.sku?.partCode
        })
      );

      this.isConfirmModalShowing = false;
    }
    return error;
  }
}
